import { makeAutoObservable, runInAction } from "mobx";
import { RootStore } from "stores";

export interface IMaterialBin {
  id: string;
  name: string;
  type: "aggregate" | "cement" | "water" | "admixture";
  currentLevel: number;
  capacity: number;
  targetWeight: number;
  actualWeight: number;
  isDischarging: boolean;
  color: string;
}

export interface IPlantStatus {
  mode: "idle" | "batching" | "loading" | "complete";
  mixerLevel: number;
  mixerRotating: boolean;
  mixerDischarging: boolean;
  conveyorRunning: boolean;
  truckLoadLevel: number;
  truckPresent: boolean;
  isSimulation: boolean;
}

export interface ICurrentTicket {
  ticketNumber: string;
  truckNumber: string;
  mixDesign: string;
  volume: number;
  customerName: string;
  jobSite: string;
}

const DEFAULT_BINS: IMaterialBin[] = [
  // Aggregates - yellow/gold colors matching 3D bins
  {
    id: "agg1",
    name: '3/4"',
    type: "aggregate",
    currentLevel: 67,
    capacity: 100,
    targetWeight: 1565,
    actualWeight: 0,
    isDischarging: false,
    color: "#c4a35a",
  },
  {
    id: "agg2",
    name: '3/8"',
    type: "aggregate",
    currentLevel: 51,
    capacity: 100,
    targetWeight: 996,
    actualWeight: 0,
    isDischarging: false,
    color: "#b89a4d",
  },
  {
    id: "agg3",
    name: "Sand",
    type: "aggregate",
    currentLevel: 70,
    capacity: 100,
    targetWeight: 998,
    actualWeight: 0,
    isDischarging: false,
    color: "#d4b86a",
  },
  {
    id: "agg4",
    name: "Fine",
    type: "aggregate",
    currentLevel: 79,
    capacity: 100,
    targetWeight: 996,
    actualWeight: 0,
    isDischarging: false,
    color: "#e8cc7a",
  },
  // Cement - gray tones for 3D silos
  {
    id: "cem1",
    name: "Type-10",
    type: "cement",
    currentLevel: 71,
    capacity: 100,
    targetWeight: 386,
    actualWeight: 0,
    isDischarging: false,
    color: "#8a9a8a",
  },
  {
    id: "cem2",
    name: "Fly Ash",
    type: "cement",
    currentLevel: 68,
    capacity: 100,
    targetWeight: 115,
    actualWeight: 0,
    isDischarging: false,
    color: "#6a7a6a",
  },
  {
    id: "cem3",
    name: "Slag",
    type: "cement",
    currentLevel: 74,
    capacity: 100,
    targetWeight: 0,
    actualWeight: 0,
    isDischarging: false,
    color: "#5a6a5a",
  },
  // Water - red for hot, blue for cold
  {
    id: "water1",
    name: "Hot",
    type: "water",
    currentLevel: 71,
    capacity: 100,
    targetWeight: 145,
    actualWeight: 0,
    isDischarging: false,
    color: "#ff6b6b",
  },
  {
    id: "water2",
    name: "Cold",
    type: "water",
    currentLevel: 56,
    capacity: 100,
    targetWeight: 0,
    actualWeight: 0,
    isDischarging: false,
    color: "#4dabf7",
  },
  // Admixtures - distinct colors
  {
    id: "adm1",
    name: "AEA",
    type: "admixture",
    currentLevel: 51,
    capacity: 100,
    targetWeight: 12,
    actualWeight: 0,
    isDischarging: false,
    color: "#ffd700",
  },
  {
    id: "adm2",
    name: "Retarder",
    type: "admixture",
    currentLevel: 80,
    capacity: 100,
    targetWeight: 8,
    actualWeight: 0,
    isDischarging: false,
    color: "#2196f3",
  },
  {
    id: "adm3",
    name: "Super-P",
    type: "admixture",
    currentLevel: 68,
    capacity: 100,
    targetWeight: 15,
    actualWeight: 0,
    isDischarging: false,
    color: "#4caf50",
  },
];

const DEFAULT_STATUS: IPlantStatus = {
  mode: "idle",
  mixerLevel: 0,
  mixerRotating: false,
  mixerDischarging: false,
  conveyorRunning: false,
  truckLoadLevel: 0,
  truckPresent: true,
  isSimulation: true,
};

const DEFAULT_TICKET: ICurrentTicket = {
  ticketNumber: "T-1001",
  truckNumber: "102",
  mixDesign: "3000AE",
  volume: 1.0,
  customerName: "ABC Construction",
  jobSite: "123 Main St",
};

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export default class BatchPlantVisualizerViewStore {
  // Observable Properties
  bins: IMaterialBin[] = [...DEFAULT_BINS.map((bin) => ({ ...bin }))];
  status: IPlantStatus = { ...DEFAULT_STATUS };
  currentTicket: ICurrentTicket | null = { ...DEFAULT_TICKET };
  selectedBinId: string | null = null;
  isRunning = false;
  isLoading = false;
  error: string | null = null;

  private rootStore: RootStore;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false } as any);
  }

  // Computed Values
  get aggregateBins(): IMaterialBin[] {
    return this.bins.filter((b) => b.type === "aggregate");
  }

  get cementBins(): IMaterialBin[] {
    return this.bins.filter((b) => b.type === "cement");
  }

  get waterBins(): IMaterialBin[] {
    return this.bins.filter((b) => b.type === "water");
  }

  get admixtureBins(): IMaterialBin[] {
    return this.bins.filter((b) => b.type === "admixture");
  }

  get selectedBin(): IMaterialBin | null {
    if (!this.selectedBinId) return null;
    return this.bins.find((b) => b.id === this.selectedBinId) || null;
  }

  get totalTargetWeight(): number {
    return this.bins.reduce((sum, bin) => sum + bin.targetWeight, 0);
  }

  get totalActualWeight(): number {
    return this.bins.reduce((sum, bin) => sum + bin.actualWeight, 0);
  }

  get batchProgress(): number {
    if (this.totalTargetWeight === 0) return 0;
    return (this.totalActualWeight / this.totalTargetWeight) * 100;
  }

  // Synchronous Actions
  selectBin = (binId: string | null) => {
    this.selectedBinId = binId;
  };

  setTruckPresent = (present: boolean) => {
    this.status.truckPresent = present;
  };

  setSimulationMode = (enabled: boolean) => {
    this.status.isSimulation = enabled;
  };

  updateTicket = (ticket: Partial<ICurrentTicket>) => {
    if (this.currentTicket) {
      this.currentTicket = { ...this.currentTicket, ...ticket };
    }
  };

  // Async Actions
  startBatch = async () => {
    if (this.isRunning) return;

    runInAction(() => {
      this.isRunning = true;
      this.status.mode = "batching";
      this.status.conveyorRunning = true;
    });

    try {
      // Discharge aggregates
      for (const bin of this.aggregateBins) {
        if (bin.targetWeight <= 0) continue;
        await this.dischargeMaterial(bin.id, 80);
      }

      await delay(500);

      // Discharge cement
      for (const bin of this.cementBins) {
        if (bin.targetWeight <= 0) continue;
        await this.dischargeMaterial(bin.id, 60);
      }

      // Discharge water
      for (const bin of this.waterBins) {
        if (bin.targetWeight <= 0) continue;
        await this.dischargeMaterial(bin.id, 40);
      }

      // Discharge admixtures
      for (const bin of this.admixtureBins) {
        if (bin.targetWeight <= 0) continue;
        await this.dischargeMaterial(bin.id, 30);
      }

      // Fill mixer
      runInAction(() => {
        this.status.mixerRotating = true;
      });

      for (let i = 0; i <= 20; i++) {
        await delay(75);
        runInAction(() => {
          this.status.mixerLevel = i * 5;
        });
      }

      runInAction(() => {
        this.status.conveyorRunning = false;
      });

      // Mixing time
      await delay(2000);

      // Discharge to truck
      runInAction(() => {
        this.status.mode = "loading";
        this.status.mixerDischarging = true;
      });

      for (let i = 0; i <= 20; i++) {
        await delay(100);
        runInAction(() => {
          this.status.mixerLevel = 100 - i * 5;
          this.status.truckLoadLevel = i * 5;
        });
      }

      runInAction(() => {
        this.status.mode = "complete";
        this.status.mixerDischarging = false;
        this.status.mixerRotating = false;
        this.isRunning = false;
      });
    } catch (error: any) {
      runInAction(() => {
        this.error = error.message || "Batch failed";
        this.isRunning = false;
      });
    }
  };

  private dischargeMaterial = async (
    binId: string,
    stepDelay: number
  ): Promise<void> => {
    const bin = this.bins.find((b) => b.id === binId);
    if (!bin) return;

    runInAction(() => {
      const binRef = this.bins.find((b) => b.id === binId);
      if (binRef) binRef.isDischarging = true;
    });

    for (let i = 0; i <= 10; i++) {
      await delay(stepDelay);
      runInAction(() => {
        const binRef = this.bins.find((b) => b.id === binId);
        if (binRef) {
          binRef.actualWeight = (binRef.targetWeight * i) / 10;
          binRef.currentLevel = Math.max(0, binRef.currentLevel - 0.5);
        }
      });
    }

    runInAction(() => {
      const binRef = this.bins.find((b) => b.id === binId);
      if (binRef) binRef.isDischarging = false;
    });
  };

  resetBatch = () => {
    this.bins = DEFAULT_BINS.map((bin) => ({
      ...bin,
      actualWeight: 0,
      currentLevel: 50 + Math.random() * 40,
      isDischarging: false,
    }));
    this.status = { ...DEFAULT_STATUS, truckPresent: true };
    this.isRunning = false;
    this.error = null;
  };

  loadVisualizerData = async () => {
    this.isLoading = true;
    this.error = null;

    try {
      // Load any real data from stores if available
      const trucksStore = (this.rootStore as any).trucksStore;
      const materialsStore = (this.rootStore as any).materialsStore;

      const promises = [];

      if (trucksStore) {
        promises.push(trucksStore.find({ query: { $limit: 50 } }));
      }

      if (materialsStore) {
        promises.push(materialsStore.find({ query: { $limit: 100 } }));
      }

      await Promise.all(promises);

      runInAction(() => {
        this.isLoading = false;
      });
    } catch (error: any) {
      runInAction(() => {
        this.error = error.message || "Failed to load data";
        this.isLoading = false;
      });
    }
  };

  clearError = () => {
    this.error = null;
  };

  reset = () => {
    this.resetBatch();
    this.selectedBinId = null;
    this.currentTicket = { ...DEFAULT_TICKET };
    this.isLoading = false;
    this.error = null;
  };
}
